import { faKey } from "@fortawesome/free-solid-svg-icons"
import { useMemo } from "react"
import { Permissions } from "../../../../Enum/PermissionsType"
import { GenericDropDown } from "../../../../components/Dropdown/Dropdown"
import { TitleChildrenPath } from "../../../../components/Titles/Titles"
import { useTranslation } from "react-i18next"
import Animate, { AnimationTypes } from "../../../../components/Animate/Animate"
import { PopupHelpButton } from "../../../../components/Help/PopupHelpButton"

type Props = {
  value: string[]
  onChange: (permissions: string[]) => void
  disabled?: boolean
}

const permissionRolePresets: Record<string, string[]> = {
  manager: [
    Permissions.CanEditeEmployee,
    Permissions.CanEditeCompany,
    Permissions.CanEditeCategories,
    Permissions.CanEditeMeals,
    Permissions.CanEditeDrinks,
    Permissions.CanEditeAddtionals,
    Permissions.CanEditeCombos,
    Permissions.CanEditeExtraService,
    Permissions.CanEditePromotions,
    Permissions.CanEditeCoupons,
    Permissions.CanEditeNotices,
    Permissions.CanEditeReservations,
    Permissions.CanEditeOrders,
    Permissions.CanEditeTables,
    Permissions.CanEditeClients,
    Permissions.CanEditeKitchen,
    Permissions.CanEditeCourier,
    Permissions.CanAnswerWhatsApp,
    Permissions.CanEditeAwards,
    Permissions.CanViewProfit,
    Permissions.CanEditeReservations,
    Permissions.CanEditeTimeClock,
    Permissions.CanChargeTapToPay,
  ],
  cashier: [
    Permissions.CanEditeOrders,
    Permissions.CanEditeTables,
    Permissions.CanEditeClients,
    Permissions.CanEditeAwards,
    Permissions.CanAnswerWhatsApp,
    Permissions.CanViewProfit,
    Permissions.CanChargeTapToPay,
  ],
  waiter: [
    Permissions.CanEditeOrders,
    Permissions.CanEditeTables,
    Permissions.CanEditeClients,
    Permissions.CanChargeTapToPay,
  ],
  kitchen: [
    Permissions.CanEditeKitchen,
    Permissions.CanEditeOrders,
    Permissions.CanEditeClients,
    Permissions.CanEditeCourier,
  ],
  catalog: [
    Permissions.CanEditeCategories,
    Permissions.CanEditeMeals,
    Permissions.CanEditeDrinks,
    Permissions.CanEditeAddtionals,
    Permissions.CanEditeCombos,
    Permissions.CanEditeExtraService,
  ],
  marketing: [
    Permissions.CanEditePromotions,
    Permissions.CanEditeCoupons,
    Permissions.CanEditeNotices,
    Permissions.CanEditeAwards,
    Permissions.CanViewProfit,
    Permissions.CanAnswerWhatsApp,
  ],
  delivery: [
    Permissions.CanEditeCourier,
    Permissions.CanEditeOrders,
    Permissions.CanEditeClients,
    Permissions.CanEditeKitchen,
  ],
}

const areSamePermissions = (first: string[], second: string[]) => {
  if (first.length !== second.length) {
    return false
  }

  const secondSet = new Set(second)

  return first.every((item) => secondSet.has(item))
}

export function EmployeePermissions({ value, onChange, disabled }: Props) {
  const { t } = useTranslation("permissions")

  const presetOptions = useMemo(
    () => [
      { label: t("rolePreset.custom"), value: "custom" },
      { label: t("rolePreset.manager"), value: "manager" },
      { label: t("rolePreset.cashier"), value: "cashier" },
      { label: t("rolePreset.waiter"), value: "waiter" },
      { label: t("rolePreset.kitchen"), value: "kitchen" },
      { label: t("rolePreset.catalog"), value: "catalog" },
      { label: t("rolePreset.marketing"), value: "marketing" },
      { label: t("rolePreset.delivery"), value: "delivery" },
    ],
    [t],
  )

  const selectedPreset = useMemo(() => {
    const matchedPreset = Object.entries(permissionRolePresets).find(([, presetPermissions]) =>
      areSamePermissions(value, presetPermissions),
    )

    return matchedPreset?.[0] ?? "custom"
  }, [value])

  const permissionsByCategory: Array<{ key: string; permissions: string[] }> = [
    {
      key: "management",
      permissions: [Permissions.CanEditeEmployee, Permissions.CanEditeCompany],
    },
    {
      key: "catalog",
      permissions: [
        Permissions.CanEditeCategories,
        Permissions.CanEditeMeals,
        Permissions.CanEditeDrinks,
        Permissions.CanEditeAddtionals,
        Permissions.CanEditeCombos,
        Permissions.CanEditeExtraService,
      ],
    },
    {
      key: "marketing",
      permissions: [Permissions.CanEditePromotions, Permissions.CanEditeCoupons, Permissions.CanEditeNotices],
    },
    {
      key: "operation",
      permissions: [
        Permissions.CanEditeOrders,
        Permissions.CanEditeTables,
        Permissions.CanEditeClients,
        Permissions.CanEditeKitchen,
        Permissions.CanEditeCourier,
        Permissions.CanEditeAwards,
        Permissions.CanAnswerWhatsApp,
        Permissions.CanEditeReservations,
        Permissions.CanEditeTimeClock,
        Permissions.CanChargeTapToPay,
      ],
    },
    {
      key: "insights",
      permissions: [Permissions.CanViewProfit],
    },
  ]

  const togglePermission = (permission: string) => {
    if (disabled) return

    if (value.includes(permission)) {
      onChange(value.filter((p) => p !== permission))
    } else {
      onChange([...value, permission])
    }
  }

  const handlePresetChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const presetValue = event.target.value

    if (presetValue === "custom") {
      return
    }

    onChange(permissionRolePresets[presetValue])
  }

  return (
    <TitleChildrenPath
      icon={faKey}
      iconColor="#000000"
      boxShadow="none"
      title={t("label_title")}
      children={
        <div
          className="
          mt-1
          p-2
          flex flex-col
          gap-4
        "
        >
          <GenericDropDown
            data={presetOptions}
            initialState={selectedPreset}
            handlerFuncion={handlePresetChange}
            disable={disabled}
            legend={t("rolePreset.legend")}
            descriptionKey="interactionDescriptions:pages.myRestaurant.employees.components.employeePermissions.select.legend"
          />

          {permissionsByCategory.map((group, groupIndex) => (
            <Animate key={group.key} type={AnimationTypes.fadeIn} duration={(0.6 + groupIndex / 5).toString() + "s"}>
              <div className="rounded-lg border border-orange-100 bg-orange-50/40 p-3 shadow-sm">
                <div className="mb-3 flex items-center justify-between gap-2">
                  <span className="text-xs font-black uppercase tracking-wide text-slate-600">
                    {t(`categories.${group.key}`)}
                  </span>

                  <PopupHelpButton
                    helpKey="pages.myrestaurant.employees.components.employeepermissions.1"
                    title={t("helpPopup:sections.employee.rules.title")}
                    description={t("helpPopup:sections.employee.rules.description")}
                    bullets={t("helpPopup:sections.employee.rules.bullets", { returnObjects: true }) as string[]}
                  />
                </div>

                <div
                  className="
                    grid
                    gap-3
                    [grid-template-columns:repeat(auto-fill,minmax(260px,1fr))]
                  "
                >
                  {group.permissions.map((permission) => {
                    const enabled = value.includes(permission)

                    return (
                      <div
                        key={permission}
                        className={`
                          flex items-center justify-between
                          px-2 py-3
                          rounded-xl
                          ring-2
                          transition-all duration-300
                          ${enabled ? "ring-orange-500 bg-orange-50 shadow-sm" : "ring-slate-200 bg-white"}
                        `}
                      >
                        <span className="text-sm font-bold text-slate-800">{t(permission)}</span>

                        <button
                          type="button"
                          disabled={disabled}
                          onClick={() => togglePermission(permission)}
                          className={`
                            relative w-[42px] h-[22px]
                            rounded-full
                            transition-all duration-300
                            ${enabled ? "bg-linear-to-r from-red-600 to-orange-500" : "bg-slate-400"}
                            ${disabled ? "opacity-50 cursor-not-allowed" : ""}
                          `}
                          data-interaction-description-key="interactionDescriptions:pages.myRestaurant.employees.components.employeePermissions.button.togglePermissionPermission"
                        >
                          <span
                            className={`
                              absolute top-[2px] left-[2px]
                              w-[18px] h-[18px]
                              bg-white rounded-full
                              shadow-sm
                              transition-all duration-300
                              ${enabled ? "translate-x-[20px]" : "translate-x-0"}
                            `}
                          />
                        </button>
                      </div>
                    )
                  })}
                </div>
              </div>
            </Animate>
          ))}
        </div>
      }
    />
  )
}
